import React, { useState } from "react";
import { Lock, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { safeJsonRequest } from "../../utils/safeFetch";

/**
 * ReAuthModal — Password re-confirmation before SSN reveal.
 * Blocks reveal until admin re-enters their password.
 */
const ReAuthModal = ({ open, onClose, onConfirm, loading, error }) => {
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    onConfirm(password);
  };

  const handleClose = () => {
    setPassword("");
    setShowPw(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="reauth-modal-overlay">
      <div className="bg-slate-800 rounded-2xl border border-slate-600 shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-5 pb-3 flex items-center gap-3">
          <div className="p-2 rounded-xl bg-amber-600/20">
            <Lock className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Re-Authentication Required</h3>
            <p className="text-xs text-slate-400">Confirm your password to reveal SSN</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-5">
          {error && (
            <div className="mb-3 p-2.5 bg-red-900/40 border border-red-700 rounded-lg flex items-start gap-2" data-testid="reauth-error">
              <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-300">{error}</p>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Admin Password</label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent pr-10"
                placeholder="Enter your password"
                autoFocus
                data-testid="reauth-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                tabIndex={-1}
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 py-2.5 bg-slate-700 text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-600 transition-colors"
              data-testid="reauth-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !password.trim()}
              className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-bold hover:bg-amber-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              data-testid="reauth-confirm"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Eye className="w-3.5 h-3.5" /> Reveal SSN
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReAuthModal;
